import {
  ChevronDown,
  ChevronRight,
  Copy,
  Eye,
  Printer,
  Search,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Badge, Button } from "reactstrap";
import LoadingMessage from "../../../../components/LoadingMessage";
import { tierDisplayNames, tierIcons } from "../../../../constants/constans";
import { getTierDisplayName } from "../../../../constants/tierConstants";
import useProductionPlanPrint, {
  ProductionPlanPrintData,
} from "../../../../hooks/useProductionPlanPrint";
import { useFirebaseProductOverview } from "../../../../hooks/useFirebaseProductOverview ";
import {
  ApiMenuItem,
  DisplayProduct,
} from "../../../../redux/allocationService";
import {
  LocalProductionPlanFormData,
  ProductionPlanItem,
  useCreateProductionPlanForAllocationMutation,
  useLazyGetProductionPlanQuery,
} from "../../../../redux/services/allocationService";
import { useLazyGetMenuItemListQuery } from "../../../../redux/services/setMenuService";
import { formatDateForAPISimple } from "../../../../utility/productUtility";
import "./index.css";

interface ProductionPlanningScreenProps {
  quantities: Record<string, number>;
  setQuantities: (quantities: Record<string, number>) => void;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  templateMode?: boolean;
  singleDateMode?: boolean;
  templateSourceDate?: string;
  templateSaveToDate?: string;
  isShowingLoadFromDate?: boolean;
  isPastDate?: boolean;
}

export function ProductionPlanningScreen({
  quantities,
  setQuantities,
  selectedDate: displayDate,
  setSelectedDate: setDisplayDate,
  templateMode = false,
  singleDateMode = false,
  templateSourceDate,
  templateSaveToDate,
  isShowingLoadFromDate = false,
  isPastDate = false,
}: ProductionPlanningScreenProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTier, setSelectedTier] = useState<string>("all");
  const [expandedTiers, setExpandedTiers] = useState<Record<string, boolean>>({
    bronze: true,
    gold: true,
    platinum: true,
    "no-tier": true,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saveSuccess, setSaveSuccess] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasExistingPlan, setHasExistingPlan] = useState<boolean>(false);
  const [isEditingPastPlan, setIsEditingPastPlan] = useState<boolean>(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const { printProductionPlan, isPrinting } = useProductionPlanPrint();

  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState<boolean>(false);
  const [lastToastId, setLastToastId] = useState<string>("");
  const shouldDisableInputs = isPastDate && singleDateMode;

  const [originalQuantities, setOriginalQuantities] = useState<
    Record<string, number>
  >({});

  const [saveToDate, setSaveToDate] = useState<string>(
    templateMode && templateSaveToDate
      ? templateSaveToDate
      : singleDateMode
        ? displayDate
        : getTodayDate(),
  );

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const restaurantName = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.name,
  );

  // ── Firebase product overview for pre-orders ──────────────────────────────
  const currentViewDate = useMemo(() => {
    if (!displayDate) return new Date();
    const [year, month, day] = displayDate.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [displayDate]);

  const { data: productOverviewData } = useFirebaseProductOverview(
    restaurantId,
    currentViewDate,
  );

  // Map itemId → net pre-orders (reserved - cancelled)
  const preOrdersMap = useMemo(() => {
    const map = new Map<string, { reserved: number; cancelled: number }>();
    if (productOverviewData && Array.isArray(productOverviewData)) {
      productOverviewData.forEach((product) => {
        if (product.itemId) {
          map.set(product.itemId, {
            reserved: product.quantities?.reserved || 0,
            cancelled: product.quantities?.cancelled || 0,
          });
        }
      });
    }
    return map;
  }, [productOverviewData]);
  // ─────────────────────────────────────────────────────────────────────────

  const isSelectedDateToday = (date: string): boolean => {
    const today = new Date();
    const selected = new Date(date);
    return (
      today.getFullYear() === selected.getFullYear() &&
      today.getMonth() === selected.getMonth() &&
      today.getDate() === selected.getDate()
    );
  };

  const isSelectedDateInPast = (date: string): boolean => {
    const today = new Date();
    const selected = new Date(date);
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const selectedStart = new Date(
      selected.getFullYear(),
      selected.getMonth(),
      selected.getDate(),
    );
    return selectedStart < todayStart;
  };

  useEffect(() => {
    const isPast =
      isSelectedDateInPast(displayDate) && !isSelectedDateToday(displayDate);
    setIsEditingPastPlan(isPast);
  }, [displayDate]);

  useEffect(() => {
    if (templateMode && templateSaveToDate) {
      setSaveToDate(templateSaveToDate);
    } else if (singleDateMode) {
      setSaveToDate(displayDate);
    }
  }, [templateMode, singleDateMode, templateSaveToDate, displayDate]);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" | "warning" = "info",
  ) => {
    if (type === "info" && lastToastId === message) return;
    if (type === "info") {
      setLastToastId(message);
    } else {
      setLastToastId("");
    }
    switch (type) {
      case "success":
        toast.success(message);
        break;
      case "error":
        toast.error(message);
        break;
      case "warning":
        toast.warning(message);
        break;
      case "info":
      default:
        toast.info(message);
        break;
    }
  };

  const [fetchMenuItems, { data: apiData, isLoading: isMenuLoading, isError }] =
    useLazyGetMenuItemListQuery();
  const [createProductionPlanForAllocation, { isLoading: isSaving }] =
    useCreateProductionPlanForAllocationMutation();

  const [
    fetchProductionPlan,
    {
      data: productionPlanData,
      isLoading: isPlanLoading,
      error: productionPlanError,
    },
  ] = useLazyGetProductionPlanQuery();

  const isLoading = useMemo(() => {
    return isMenuLoading || isPlanLoading || isLoadingInternal || !dataLoaded;
  }, [isMenuLoading, isPlanLoading, isLoadingInternal, dataLoaded]);

  useEffect(() => {
    fetchMenuItems({ restaurantId });
    setIsInitialLoad(true);
    setDataLoaded(false);
    setIsLoadingInternal(false);
    setInitialLoadComplete(false);
  }, [fetchMenuItems, restaurantId]);

  useEffect(() => {
    setDataLoaded(false);
    setIsLoadingInternal(false);
    setIsInitialLoad(true);
    setSearchQuery("");
    setSelectedTier("all");
    setIsEditingPastPlan(
      isSelectedDateInPast(displayDate) && !isSelectedDateToday(displayDate),
    );
    setLastToastId("");
    setInitialLoadComplete(false);
  }, [displayDate]);

  useEffect(() => {
    return () => {
      setDataLoaded(false);
      setIsLoadingInternal(false);
      setLastToastId("");
      setIsInitialLoad(true);
      setInitialLoadComplete(false);
    };
  }, []);

  useEffect(() => {
    if (displayDate && restaurantId && apiData) {
      if (dataLoaded) return;
      setIsLoadingInternal(true);
      const formattedDate = formatDateForAPISimple(displayDate);
      fetchProductionPlan({ restaurantId, date: formattedDate })
        .unwrap()
        .then(() => setIsLoadingInternal(false))
        .catch((error) => {
          setIsLoadingInternal(false);
          console.error("Error fetching production plan:", error);
        });
    }
  }, [displayDate, restaurantId, fetchProductionPlan, apiData, dataLoaded]);

  const detectTier = (
    item: ApiMenuItem,
  ): "gold" | "platinum" | "bronze" | "" => {
    if (item.tags?.class?.includes("platinum")) return "platinum";
    if (item.tags?.class?.includes("gold")) return "gold";
    if (item.tags?.class?.includes("bronze")) return "bronze";

    const nameLower = (item.displayName || "").toLowerCase();
    if (nameLower.includes("(platinum)")) return "platinum";
    if (nameLower.includes("(gold)")) return "gold";
    if (nameLower.includes("(bronze)")) return "bronze";

    const categoryLower = (item.category || "").toLowerCase();
    if (categoryLower.includes("platinum")) return "platinum";
    if (categoryLower.includes("gold")) return "gold";
    if (categoryLower.includes("bronze")) return "bronze";

    return "";
  };

  const products: DisplayProduct[] = useMemo(() => {
    if (!apiData) return [];

    const allProducts: DisplayProduct[] = [];
    const seenIds = new Set<string>();

    apiData.forEach((item: ApiMenuItem) => {
      const tier = detectTier(item);
      const basePrice = item.price || 0;

      if (seenIds.has(item.itemId)) return;
      seenIds.add(item.itemId);

      if (item.portions && item.portions.length > 0) {
        const firstPortion = item.portions[0];
        const portionSize = firstPortion.displayName || "REGULAR";
        const portionAdditionalCost = firstPortion.additionalCost || 0;
        const portionPrice = basePrice + portionAdditionalCost;

        allProducts.push({
          id: item.itemId,
          originalItemId: item.itemId,
          displayName: item.displayName,
          productId: item.productId,
          category: item.category || "Uncategorized",
          basePrice,
          portionPrice,
          tier,
          portionSize: portionSize.toUpperCase(),
          isAvailable: item.isAvailable,
          portionAdditionalCost,
          itemType: item.itemType,
          abbreviation: item.abbreviation || "",
          skuId: item.skuId || "",
        });
      } else {
        allProducts.push({
          id: item.itemId,
          originalItemId: item.itemId,
          displayName: item.displayName,
          productId: item.productId,
          category: item.category || "Uncategorized",
          basePrice,
          portionPrice: basePrice,
          tier,
          portionSize: "REGULAR",
          isAvailable: item.isAvailable,
          portionAdditionalCost: 0,
          itemType: item.itemType,
          abbreviation: item.abbreviation || "",
          skuId: item.skuId || "",
        });
      }
    });

    const portionOrder = { SMALL: 0, REGULAR: 1, LARGE: 2, PREMIUM: 3 };

    return allProducts.sort((a, b) => {
      if (a.tier !== b.tier) {
        const tierOrder = { platinum: 0, gold: 1, bronze: 2, "": 3 };
        return tierOrder[a.tier] - tierOrder[b.tier];
      }
      if (a.category !== b.category)
        return a.category.localeCompare(b.category);
      if (a.displayName !== b.displayName)
        return a.displayName.localeCompare(b.displayName);
      if (a.productId !== b.productId)
        return a.productId.localeCompare(b.productId);
      const aOrder =
        portionOrder[a.portionSize as keyof typeof portionOrder] ?? 99;
      const bOrder =
        portionOrder[b.portionSize as keyof typeof portionOrder] ?? 99;
      return aOrder - bOrder;
    });
  }, [apiData]);

  useEffect(() => {
    if (isLoadingInternal || !products.length) return;

    if (productionPlanError) {
      const errorData = productionPlanError as any;
      if (errorData.status === 404 || errorData.originalStatus === 404) {
        const zeroQuantities: Record<string, number> = {};
        products.forEach((p) => {
          zeroQuantities[p.id] = 0;
        });
        setQuantities(zeroQuantities);
        setOriginalQuantities({});
        setHasExistingPlan(false);
        setDataLoaded(true);
        setInitialLoadComplete(true);
        const msg =
          errorData.data?.message ||
          `No production plan found for ${displayDate}`;
        showToast(msg, "info");
      } else if (errorData.data?.message) {
        showToast(errorData.data.message, "error");
        setDataLoaded(true);
        setInitialLoadComplete(true);
      }
      setIsInitialLoad(false);
      return;
    }

    if (productionPlanData?.items && products.length > 0) {
      const newQuantities: Record<string, number> = {};
      const newOriginalQuantities: Record<string, number> = {};
      let matchCount = 0;

      products.forEach((p) => {
        newQuantities[p.id] = 0;
      });

      productionPlanData.items.forEach((planItem: ProductionPlanItem) => {
        const match = products.find(
          (p) => p.originalItemId === planItem.itemId,
        );
        if (match) {
          newQuantities[match.id] = planItem.plannedQty;
          newOriginalQuantities[match.id] = planItem.plannedQty;
          matchCount++;
        }
      });

      setQuantities(newQuantities);
      setOriginalQuantities(newOriginalQuantities);
      setHasExistingPlan(matchCount > 0);
      setDataLoaded(true);
      setInitialLoadComplete(true);

      if (matchCount > 0 && isInitialLoad && !initialLoadComplete) {
        if (templateMode && templateSourceDate && isShowingLoadFromDate) {
          showToast(
            `Loaded template data from ${templateSourceDate} for ${displayDate}. You can edit and save to ${saveToDate}.`,
            "info",
          );
        } else if (singleDateMode && displayDate !== getTodayDate()) {
          showToast(
            `Loaded data for ${displayDate}. You can edit and save to this date.`,
            "info",
          );
        } else if (
          isSelectedDateInPast(displayDate) &&
          !isSelectedDateToday(displayDate)
        ) {
          showToast(
            `Loaded past production plan for ${displayDate}. You can edit and save to a different date.`,
            "info",
          );
        } else {
          showToast(`Loaded production plan for ${displayDate}`, "success");
        }
        setIsInitialLoad(false);
      } else if (
        matchCount === 0 &&
        !isSelectedDateToday(displayDate) &&
        !initialLoadComplete
      ) {
        showToast(`No production plan found for ${displayDate}`, "info");
        setIsInitialLoad(false);
      } else {
        setIsInitialLoad(false);
      }
    } else if (
      (productionPlanData === null ||
        productionPlanData?.items?.length === 0) &&
      !isLoadingInternal
    ) {
      const zeroQuantities: Record<string, number> = {};
      products.forEach((p) => {
        zeroQuantities[p.id] = 0;
      });
      setQuantities(zeroQuantities);
      setOriginalQuantities({});
      setHasExistingPlan(false);
      setDataLoaded(true);
      setInitialLoadComplete(true);
      if (isInitialLoad) setIsInitialLoad(false);
      if (
        !isSelectedDateToday(displayDate) &&
        isSelectedDateInPast(displayDate) &&
        !initialLoadComplete
      ) {
        showToast(
          `No production plan found for ${displayDate}. You can create a new plan and save to any date.`,
          "info",
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productionPlanData,
    productionPlanError,
    products,
    displayDate,
    isInitialLoad,
    templateMode,
    singleDateMode,
    templateSourceDate,
    isShowingLoadFromDate,
    saveToDate,
    isLoadingInternal,
    dataLoaded,
    setQuantities,
    initialLoadComplete,
  ]);

  useEffect(() => {
    setSaveSuccess(false);
    setSaveError(null);
  }, [quantities]);

  const productsByTier = useMemo(() => {
    const grouped: Record<string, DisplayProduct[]> = {
      bronze: [],
      gold: [],
      platinum: [],
      "no-tier": [],
    };

    const seenInGroup = new Set<string>();

    products.forEach((product) => {
      if (seenInGroup.has(product.id)) {
        console.warn("Duplicate product ID detected in grouping:", product.id);
        return;
      }
      seenInGroup.add(product.id);
      const tierKey = product.tier || "no-tier";
      if (grouped[tierKey]) grouped[tierKey].push(product);
    });

    Object.keys(grouped).forEach((key) => {
      if (grouped[key].length === 0) delete grouped[key];
    });

    return grouped;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const filteredByTier: Record<string, DisplayProduct[]> = {};
    const trimmedQuery = searchQuery.trim().toLowerCase();

    Object.entries(productsByTier).forEach(([tierKey, tierProducts]) => {
      if (selectedTier !== "all" && tierKey !== selectedTier) return;

      const filtered =
        trimmedQuery.length > 0
          ? tierProducts.filter(
              (p) =>
                (p.displayName?.toLowerCase() || "").includes(trimmedQuery) ||
                (p.productId?.toLowerCase() || "").includes(trimmedQuery) ||
                (p.category?.toLowerCase() || "").includes(trimmedQuery) ||
                (p.portionSize?.toLowerCase() || "").includes(trimmedQuery),
            )
          : [...tierProducts];

      if (filtered.length > 0) {
        filteredByTier[tierKey] = filtered;
      }
    });

    return filteredByTier;
  }, [searchQuery, selectedTier, productsByTier]);

  const totalFilteredCount = Object.values(filteredProducts).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const toggleTier = (tier: string) => {
    setExpandedTiers((prev) => ({ ...prev, [tier]: !prev[tier] }));
  };

  const totalPlanned = Object.values(quantities).reduce((a, b) => a + b, 0);

  const tierTotals = useMemo(() => {
    const totals = { bronze: 0, gold: 0, platinum: 0 };
    products.forEach((product) => {
      if (product.tier && product.tier in totals) {
        totals[product.tier as keyof typeof totals] +=
          quantities[product.id] || 0;
      }
    });
    return totals;
  }, [quantities, products]);

  const prepareProductionPlanData = (): LocalProductionPlanFormData => {
    const productionItems: ProductionPlanItem[] = [];
    products.forEach((product) => {
      const currentQty = quantities[product.id] || 0;
      const originalQty = originalQuantities[product.id] || 0;
      if (currentQty > 0 || (currentQty === 0 && originalQty > 0)) {
        productionItems.push({
          itemId: product.originalItemId,
          displayName: product.displayName,
          plannedQty: currentQty,
          productId: product.productId,
          itemType: product.itemType,
          category: product.category,
          portionSize: product.portionSize,
          unitPrice: product.portionPrice,
          abbreviation: product.abbreviation || "",
          classFilterTag: product.tier || undefined,
          skuId: product.skuId || "",
        });
      }
    });
    return { restaurantId, items: productionItems };
  };

  const handlePrint = () => {
    if (!hasExistingPlan) {
      showToast(
        "No saved production plan to print. Please save first.",
        "warning",
      );
      return;
    }
    const printData: ProductionPlanPrintData = {
      date: saveToDate,
      items: prepareProductionPlanData().items,
      totalPlanned,
      tierTotals,
      restaurantId,
      restaurantName,
      sourceDate:
        templateMode && templateSourceDate ? templateSourceDate : undefined,
      isTemplateMode: templateMode,
      isSourceMode: templateMode && isShowingLoadFromDate,
    };
    printProductionPlan(printData);
  };

  const handleSave = async () => {
    try {
      setSaveSuccess(false);
      setSaveError(null);
      const payload = prepareProductionPlanData();
      if (payload.items.length === 0) {
        showToast(
          "Please add quantities to at least one product before saving.",
          "warning",
        );
        return;
      }
      const formattedDate = formatDateForAPISimple(saveToDate);
      await createProductionPlanForAllocation({
        restaurantId,
        date: formattedDate,
        items: payload.items,
      }).unwrap();
      setSaveSuccess(true);
      setHasExistingPlan(true);
      setOriginalQuantities({ ...quantities });
      showToast(
        hasExistingPlan
          ? `Production plan updated for ${saveToDate}`
          : `Production plan created for ${saveToDate}`,
        "success",
      );
      fetchProductionPlan({ restaurantId, date: formattedDate });
    } catch (error: any) {
      console.error("Save failed:", error);
      showToast(
        error?.data?.message ||
          error?.message ||
          "Failed to save production plan",
        "error",
      );
    }
  };

  if (!initialLoadComplete && isLoading) {
    return (
      <div
        className="production-planning-screen"
        style={{ minHeight: "400px" }}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <LoadingMessage />
        </div>
      </div>
    );
  }

  return (
    <div className="production-planning-screen">
      <div className="production-planning-screen__header">
        <div>
          <h1 className="production-planning-screen__title">
            Daily Production Plan (UC-P1)
            {templateMode ? (
              <Badge color="info" className="ms-2">
                <Copy size={12} className="me-1" />
                Template Mode{isShowingLoadFromDate ? " (Source)" : ""}
              </Badge>
            ) : singleDateMode ? (
              <Badge color="warning" className="ms-2">
                <Eye size={12} className="me-1" />
                Viewing Date
              </Badge>
            ) : null}
          </h1>
          <div className="date-display-info">
            {templateMode ? (
              <div className="template-info">
                <span className="text-info">
                  <i className="fas fa-copy me-1"></i>
                  Template Mode:{" "}
                  {isShowingLoadFromDate
                    ? "Showing source data from"
                    : "Showing target data for"}{" "}
                  <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create plan for: <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : singleDateMode ? (
              <div className="single-date-info">
                <span className="text-warning">
                  <i className="fas fa-calendar me-1"></i>
                  Viewing date: <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create plan for: <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : isEditingPastPlan ? (
              <div className="text-info mt-1 small">
                <i className="fas fa-history me-1"></i>
                Loading from past plan. You can edit and save to a different
                date.
              </div>
            ) : hasExistingPlan && isSelectedDateToday(displayDate) ? (
              <div className="text-success mt-1 small">
                <i className="fas fa-calendar-day me-1"></i>
                Loading today's production plan
              </div>
            ) : null}
          </div>
        </div>

        <div className="production-planning-screen__actions">
          <Button
            className="production-planning-screen__print-btn me-2 mt-3"
            onClick={handlePrint}
            disabled={
              isPrinting || !hasExistingPlan || isLoading || shouldDisableInputs
            }
            color="secondary"
            title={
              shouldDisableInputs
                ? "Cannot print past dates"
                : !hasExistingPlan
                  ? "No saved plan to print"
                  : `Print production plan for ${saveToDate}`
            }
          >
            {isPrinting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Printing...
              </>
            ) : (
              <>
                <Printer
                  size={16}
                  className="production-planning-screen__button-icon"
                />
                Print
              </>
            )}
          </Button>

          <Button
            className="production-planning-screen__save-btn"
            onClick={handleSave}
            disabled={isSaving || isLoading || shouldDisableInputs}
            color={
              templateMode
                ? "info"
                : singleDateMode
                  ? "warning"
                  : displayDate !== saveToDate
                    ? "info"
                    : "primary"
            }
            title={
              shouldDisableInputs
                ? "Cannot modify past dates"
                : templateMode
                  ? `Confirm to template date: ${saveToDate}`
                  : `Confirm production plan to ${saveToDate}`
            }
          >
            {shouldDisableInputs ? (
              <>
                <Eye
                  size={16}
                  className="production-planning-screen__button-icon"
                />
                View Only
              </>
            ) : isSaving ? (
              "Confirm..."
            ) : templateMode ? (
              `Confirm to ${saveToDate}`
            ) : singleDateMode ? (
              `Confirm to ${saveToDate}`
            ) : (
              "Confirm"
            )}
          </Button>
        </div>
      </div>

      <div className="production-planning-screen__filters">
        <div className="production-planning-screen__search-container">
          <Search className="production-planning-screen__search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="production-planning-screen__search-input"
            disabled={isLoading}
          />
          {searchQuery && (
            <button
              type="button"
              className="production-planning-screen__search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="production-planning-screen__tier-filters">
          {["all", "bronze", "gold", "platinum"].map((tier) => (
            <button
              key={tier}
              onClick={() => setSelectedTier(tier)}
              className={`production-planning-screen__tier-filter-btn ${
                selectedTier === tier
                  ? "production-planning-screen__tier-filter-btn--active"
                  : ""
              }`}
              disabled={isLoading}
            >
              {tier === "all"
                ? "All"
                : `${tierIcons[tier]} ${tierDisplayNames[tier]}`}
            </button>
          ))}
        </div>
      </div>

      <div className="production-planning-screen__table-container">
        <table className="production-planning-screen__table">
          <thead>
            <tr>
              <th
                className="production-planning-screen__table-header"
                style={{ width: "7%" }}
              >
                Tier
              </th>
              <th
                className="production-planning-screen__table-header"
                style={{ width: "28%" }}
              >
                Product
              </th>
              <th
                className="production-planning-screen__table-header production-planning-screen__table-cell--center"
                style={{ width: "13%" }}
              >
                SKU ID
              </th>
              <th
                className="production-planning-screen__table-header production-planning-screen__table-cell--center"
                style={{ width: "10%" }}
              >
                Size
              </th>
              <th
                className="production-planning-screen__table-header production-planning-screen__table-cell--center"
                style={{ width: "12%" }}
              >
                Price (LKR)
              </th>
              {/* ── NEW Pre-Orders column ── */}
              <th
                className="production-planning-screen__table-header production-planning-screen__table-cell--center"
                style={{ width: "12%" }}
                title="Reserved orders minus cancellations for this product today"
              >
                Pre-Orders
              </th>
              <th
                className="production-planning-screen__table-header production-planning-screen__table-cell--center"
                style={{ width: "18%" }}
              >
                Planned Qty
              </th>
            </tr>
          </thead>
          <tbody>
            {isError ? (
              <tr>
                <td colSpan={7} className="text-center py-5">
                  <div className="text-danger">
                    Failed to load products. Please try again.
                  </div>
                </td>
              </tr>
            ) : totalFilteredCount === 0 && !isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-4">
                  {searchQuery.trim()
                    ? `No products match "${searchQuery.trim()}"`
                    : "No products available"}
                </td>
              </tr>
            ) : (
              Object.entries(filteredProducts).map(
                ([tierKey, tierProducts]) => {
                  const tierTotal = tierProducts.reduce(
                    (sum, p) => sum + (quantities[p.id] || 0),
                    0,
                  );
                  const tierIcon =
                    tierKey === "no-tier"
                      ? ""
                      : tierIcons[tierKey as keyof typeof tierIcons];

                  return (
                    <React.Fragment key={tierKey}>
                      <tr
                        className="production-planning-screen__table-row--category"
                        onClick={() => toggleTier(tierKey)}
                      >
                        <td colSpan={7}>
                          <div className="production-planning-screen__category-header">
                            <div className="production-planning-screen__category-info">
                              <div className="production-planning-screen__category-left">
                                {expandedTiers[tierKey] ? (
                                  <ChevronDown className="production-planning-screen__chevron-icon" />
                                ) : (
                                  <ChevronRight className="production-planning-screen__chevron-icon" />
                                )}
                                <span className="production-planning-screen__tier-name">
                                  {tierIcon && (
                                    <span className="production-planning-screen__tier-icon-header">
                                      {tierIcon}
                                    </span>
                                  )}
                                  {getTierDisplayName(tierKey) ||
                                    "General Products"}
                                  {(templateMode || singleDateMode) &&
                                    tierKey !== "no-tier" && (
                                      <Badge
                                        color={
                                          templateMode ? "info" : "warning"
                                        }
                                        className="ms-2"
                                        size="sm"
                                      >
                                        {templateMode
                                          ? isShowingLoadFromDate
                                            ? "Source"
                                            : "Template"
                                          : "Viewing"}
                                      </Badge>
                                    )}
                                </span>
                              </div>
                              <div className="production-planning-screen__category-right">
                                <span className="production-planning-screen__category-stats">
                                  {tierProducts.length} product
                                  {tierProducts.length !== 1 ? "s" : ""} •{" "}
                                  {tierTotal} unit{tierTotal !== 1 ? "s" : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {expandedTiers[tierKey] &&
                        tierProducts.map((product, idx) => {
                          const quantity = quantities[product.id] || 0;
                          const hasPlanQty = quantity > 0;

                          // ── Pre-order data for this product ──
                          const preOrderData = preOrdersMap.get(
                            product.originalItemId,
                          );
                          const reserved = preOrderData?.reserved || 0;
                          const cancelled = preOrderData?.cancelled || 0;
                          const netPreOrders = Math.max(
                            0,
                            reserved - cancelled,
                          );
                          // ─────────────────────────────────────

                          return (
                            <tr
                              key={product.id}
                              className={`production-planning-screen__table-row--product ${
                                idx % 2 === 0
                                  ? "production-planning-screen__table-row--product-even"
                                  : "production-planning-screen__table-row--product-odd"
                              } ${
                                hasPlanQty
                                  ? "production-planning-screen__table-row--has-plan"
                                  : ""
                              }`}
                            >
                              <td className="production-planning-screen__table-cell--tier">
                                <div className="production-planning-screen__tier-container">
                                  {product.tier && (
                                    <span className="production-planning-screen__tier-icon">
                                      {tierIcons[product.tier]}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="production-planning-screen__table-cell--product-name">
                                <div className="production-planning-screen__product-name">
                                  {product.displayName}
                                  {hasPlanQty && hasExistingPlan && (
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: templateMode
                                          ? "#17a2b8"
                                          : singleDateMode
                                            ? "#ffc107"
                                            : "#28a745",
                                        marginLeft: "8px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {templateMode
                                        ? isShowingLoadFromDate
                                          ? "📋 Source"
                                          : "🎯 Template"
                                        : singleDateMode
                                          ? "Viewing"
                                          : "✓ Planned"}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="production-planning-screen__table-cell--sku production-planning-screen__table-cell--center">
                                <code className="production-planning-screen__sku-code">
                                  {product.skuId || "No SKU"}
                                </code>
                              </td>
                              <td className="production-planning-screen__table-cell--size production-planning-screen__table-cell--center">
                                <Badge>{product.portionSize}</Badge>
                              </td>
                              <td className="production-planning-screen__table-cell--price production-planning-screen__table-cell--center">
                                <span className="production-planning-screen__price-text">
                                  {product.portionPrice.toFixed(2)}
                                </span>
                                {product.portionAdditionalCost > 0 && (
                                  <div className="production-planning-screen__price-additional">
                                    (+{product.portionAdditionalCost})
                                  </div>
                                )}
                              </td>

                              {/* ── NEW Pre-Orders cell ── */}
                              <td className="production-planning-screen__table-cell--center">
                                {netPreOrders > 0 ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Badge
                                      color="secondary"
                                      title={`Reserved: ${reserved} | Cancelled: ${cancelled} | Net: ${netPreOrders}`}
                                      style={{ fontSize: "11px" }}
                                    >
                                      {netPreOrders}
                                    </Badge>
                                    {cancelled > 0 && (
                                      <span
                                        style={{
                                          fontSize: "9px",
                                          color: "#6c757d",
                                        }}
                                        title={`${cancelled} cancellation(s)`}
                                      >
                                        (−{cancelled})
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#ced4da",
                                    }}
                                  >
                                    —
                                  </span>
                                )}
                              </td>
                              {/* ─────────────────────── */}

                              <td className="production-planning-screen__table-cell--quantity production-planning-screen__table-cell--center">
                                <div className="production-planning-screen__quantity-container">
                                  <input
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    value={quantity}
                                    onChange={(e) => {
                                      if (shouldDisableInputs) return;
                                      setQuantities({
                                        ...quantities,
                                        [product.id]:
                                          Number.parseInt(e.target.value) || 0,
                                      });
                                    }}
                                    className={`production-planning-screen__quantity-input ${
                                      hasPlanQty
                                        ? "production-planning-screen__quantity-input--has-value"
                                        : ""
                                    } ${
                                      shouldDisableInputs
                                        ? "production-planning-screen__quantity-input--disabled"
                                        : ""
                                    }`}
                                    disabled={isLoading || shouldDisableInputs}
                                    style={{
                                      MozAppearance: "textfield",
                                      appearance: "textfield",
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                  />
                                  <span className="production-planning-screen__quantity-label">
                                    packs
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                },
              )
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && (
        <>
          <div className="production-planning-screen__footer-totals">
            <div className="production-planning-screen__footer-totals-content">
              <span className="production-planning-screen__footer-totals-label">
                Total Planned Production:
              </span>
              <Badge className="production-planning-screen__footer-totals-badge">
                {totalPlanned} packs
              </Badge>
              {templateMode && (
                <span className="ms-3 text-info small">
                  <i className="fas fa-copy me-1"></i>
                  {isShowingLoadFromDate ? "Source" : "Template"} data for{" "}
                  {displayDate} • Save to: {saveToDate}
                </span>
              )}
              {singleDateMode && saveToDate !== displayDate && (
                <span className="ms-3 text-warning small">
                  <i className="fas fa-eye me-1"></i>
                  Viewing data for {displayDate} • Save to: {saveToDate}
                </span>
              )}
              {!templateMode &&
                !singleDateMode &&
                displayDate !== saveToDate && (
                  <span className="ms-3 text-info small">
                    <i className="fas fa-info-circle me-1"></i>
                    Save will create plan for {saveToDate} with these quantities
                  </span>
                )}
            </div>
          </div>

          <div className="production-planning-screen__summary-cards">
            <div className="production-planning-screen__summary-card production-planning-screen__summary-card--total">
              <div className="production-planning-screen__summary-card-label">
                Total Planned
              </div>
              <div className="production-planning-screen__summary-card-value">
                {totalPlanned}
              </div>
              <div className="production-planning-screen__summary-card-unit">
                packs
              </div>
            </div>
            <div className="production-planning-screen__summary-card production-planning-screen__summary-card--bronze">
              <div className="production-planning-screen__summary-card-label">
                {tierIcons.bronze} Bronze
              </div>
              <div className="production-planning-screen__summary-card-value">
                {tierTotals.bronze}
              </div>
              <div className="production-planning-screen__summary-card-unit">
                packs
              </div>
            </div>
            <div className="production-planning-screen__summary-card production-planning-screen__summary-card--gold">
              <div className="production-planning-screen__summary-card-label">
                {tierIcons.gold} Gold
              </div>
              <div className="production-planning-screen__summary-card-value">
                {tierTotals.gold}
              </div>
              <div className="production-planning-screen__summary-card-unit">
                packs
              </div>
            </div>
            <div className="production-planning-screen__summary-card production-planning-screen__summary-card--platinum">
              <div className="production-planning-screen__summary-card-label">
                {tierIcons.platinum} Platinum
              </div>
              <div className="production-planning-screen__summary-card-value">
                {tierTotals.platinum}
              </div>
              <div className="production-planning-screen__summary-card-unit">
                packs
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}